import { prepareFrontsList, prepareHandlesList } from './wardrobe';

const prepareForFreeCalc = (price, product) => {
  const freeAccessories = Object.values(product.free_rods);

  return {
    free: freeAccessories,
  };
};

const calculatePrice = wardrobe => {
  const price = {
    overall: {
      actual: 0,
      regular: 0,
      omnibus: 0,
    },
    fronts: {
      actual: 0,
      regular: 0,
      omnibus: 0,
    },
    accessories: {
      actual: 0,
      regular: 0,
      omnibus: 0,
      forFree: {
        free: [],
      },
    },
    addons: {
      actual: 0,
      regular: 0,
      omnibus: 0,
    },
    delivery: [],
  };

  const getOmnibusPrice = priceObject => {
    // * return 0 as default if omnibus isn't a number
    if (!Number.isFinite(priceObject?.omnibus)) return 0;

    // * return actual price if omnibus is 0
    if (priceObject?.omnibus === 0) {
      return priceObject.actual;
    }

    return priceObject.omnibus;
  };

  const calcBasePrice = product => {
    if (product) {
      price.overall.actual += product.price.actual;
      price.overall.regular += product.price.regular;
      price.overall.omnibus += getOmnibusPrice(product.price);

      price.delivery.push(product.delivery_time ?? 0);
    }
  };

  const calcAddonsPrice = product => {
    if (Object.prototype.hasOwnProperty.call(product, 'addons')) {
      const params = {
        hasBrake: product.addons?.hasOwnProperty('brake') && !!product.addons.brake,
        hasFronts: product.addons?.hasOwnProperty('fronts') && !!product.addons.fronts,
        hasExternalLight:
          product.addons?.hasOwnProperty('external_light') && !!product.addons.external_light,
      };

      params.hasAddons =
        params.hasBrake || (params.hasFronts && !product.withoutDoor) || params.hasExternalLight;

      if (params.hasAddons) {
        if (params.hasBrake && !!product.addons.brake) {
          price.overall.actual += product.addons.brake.price.actual;
          price.overall.regular += product.addons.brake.price.regular;
          price.overall.omnibus += getOmnibusPrice(product.addons.brake.price);

          price.addons.regular += product.addons.brake.price.regular;
          price.addons.actual += product.addons.brake.price.actual;
          price.addons.omnibus += getOmnibusPrice(product.addons.brake.price);

          price.delivery.push(product.addons.brake.delivery_time ?? 0);
        }

        if (params.hasExternalLight && !!product.addons.external_light) {
          price.overall.actual += product.addons.external_light.price.actual;
          price.overall.regular += product.addons.external_light.price.regular;
          price.overall.omnibus += getOmnibusPrice(product.addons.external_light.price);

          price.addons.regular += product.addons.external_light.price.regular;
          price.addons.actual += product.addons.external_light.price.actual;
          price.addons.omnibus += getOmnibusPrice(product.addons.external_light.price);

          price.delivery.push(product.addons.external_light.delivery_time ?? 0);
        }

        if (params.hasFronts && !!product.addons.fronts) {
          Object.values(product.addons.fronts).forEach(item => {
            price.overall.actual += 2 * item.price.actual;
            price.overall.regular += 2 * item.price.regular;
            price.overall.omnibus += 2 * getOmnibusPrice(item.price);

            price.addons.regular += 2 * item.price.regular;
            price.addons.actual += 2 * item.price.actual;
            price.addons.omnibus += 2 * getOmnibusPrice(item.price);

            price.delivery.push(item.delivery_time ?? 0);
          });
        }
      }
    }
  };

  const calcAccessoriesPrice = product => {
    const add = cost => {
      price.overall.actual += cost.actual;
      price.overall.regular += cost.regular;
      price.overall.omnibus += getOmnibusPrice(cost);
      price.accessories.actual += cost.actual;
      price.accessories.regular += cost.regular;
      price.accessories.omnibus += getOmnibusPrice(cost);
    };

    const checkIsFree = item => {
      const findItem = price.accessories.forFree.free.find(el => el.slug === item.slug);

      if (findItem) {
        price.accessories.forFree.free = price.accessories.forFree.free.filter(
          el => el !== findItem,
        );

        add({ actual: 0, regular: 0, omnibus: 0 });
        return {
          ...item,
          gratis: true,
          calculateActualPrice: 0,
        };
      }

      add(item.price);
      return {
        ...item,
        gratis: false,
        calculateActualPrice: item.price.actual,
      };
    };

    if (product) {
      Object.keys(product.accessories).forEach(key => {
        const accessories = product.accessories[key];

        if (accessories.length) {
          product.accessories[key] = accessories.map(item => {
            if (item.free) {
              const el = checkIsFree(item);
              return el;
            }
            add(item.price);
            price.delivery.push(item.delivery_time ?? 0);
            return item;
          });
        }
      });
    }
  };

  const calcFrontsPrice = product => {
    const hasTrack = product.addons?.hasOwnProperty('track') && !!product.addons.track;

    if (!!product.fronts) {
      const fronts = prepareFrontsList(product, {});

      fronts.forEach((item, key) => {
        if (item !== null && item.hasOwnProperty('safo')) {
          price.overall.actual += item.price.actual;
          price.overall.regular += item.price.regular;
          price.overall.omnibus += getOmnibusPrice(item.price);
          price.fronts.actual += item.price.actual;
          price.fronts.regular += item.price.regular;
          price.fronts.omnibus += getOmnibusPrice(item.price);

          price.delivery.push(item.delivery_time ?? 0);
        }
      });
    }

    if (hasTrack) {
      price.overall.actual += product.addons.track.price.actual;
      price.overall.regular += product.addons.track.price.regular;
      price.overall.omnibus += getOmnibusPrice(product.addons.track.price);

      price.fronts.regular += product.addons.track.price.regular;
      price.fronts.actual += product.addons.track.price.actual;
      price.fronts.omnibus += getOmnibusPrice(product.addons.track.price);

      price.delivery.push(product.addons.track.delivery_time ?? 0);
    }

    if (product.handles) {
      const handles = prepareHandlesList(product);
      handles.forEach(handle => {
        if (handle) {
          price.overall.actual += handle.price.actual;
          price.overall.regular += handle.price.regular;
          price.overall.omnibus += getOmnibusPrice(handle.price);
          price.fronts.actual += handle.price.actual;
          price.fronts.regular += handle.price.regular;
          price.fronts.omnibus += getOmnibusPrice(handle.price);

          price.delivery.push(handle.delivery_time ?? 0);
        }
      });
    }
  };

  const calc = item => {
    price.accessories.forFree = prepareForFreeCalc(price, item);
    calcBasePrice(item);
    calcAccessoriesPrice(item);
    if (!item.withoutDoor) {
      calcFrontsPrice(item);
    }
    calcAddonsPrice(item);
  };

  if (Array.isArray(wardrobe)) {
    wardrobe.forEach(calc);
  } else {
    calc(wardrobe);
  }

  price.delivery = Math.max(...price.delivery);

  return price;
};

export default calculatePrice;
